/**
 * Vocabulary layer — installer-to-homeowner term translation.
 * Based on docs/core/INSTALLER_VS_HOMEOWNER_SURFACE_MAP.md §9.
 *
 * Usage: t('stale', isHomeowner) → "Data outdated" or "Stale"
 */

const VOCABULARY: Record<string, { installer: string; homeowner: string }> = {
  // Status terms
  stale: { installer: 'Stale', homeowner: 'Data outdated' },
  offline: { installer: 'Offline', homeowner: 'Not connected' },
  live: { installer: 'Live', homeowner: 'Connected' },
  poll: { installer: 'Poll', homeowner: 'Refresh' },
  pollNow: { installer: 'Poll Now', homeowner: 'Refresh Data' },
  polling: { installer: 'Polling...', homeowner: 'Updating...' },
  lastPoll: { installer: 'Last poll', homeowner: 'Last updated' },
  neverPolled: { installer: 'Never polled', homeowner: 'No data yet' },
  pollFailed: { installer: 'Poll failed', homeowner: 'Update failed' },

  // Energy terms
  soc: { installer: 'SOC', homeowner: 'Battery level' },
  telemetry: { installer: 'Telemetry', homeowner: 'System data' },
  telemetryTimeline: { installer: 'Telemetry Timeline', homeowner: 'Energy History' },
  telemetryHistory: { installer: 'Telemetry History', homeowner: 'Energy History' },
  noTelemetry: { installer: 'No Telemetry', homeowner: 'No system data' },
  runBackfill: { installer: 'Run backfill to import historical data.', homeowner: 'Import history to see past data.' },

  // Risk/alert terms
  curtailment: { installer: 'PV Curtailment', homeowner: 'Solar Limited' },
  curtailmentCleared: { installer: 'Curtailment Cleared', homeowner: 'Solar No Longer Limited' },
  resilienceRisk: { installer: 'Resilience Risk', homeowner: 'Backup Power at Risk' },
  breach: { installer: 'Reserve breach', homeowner: 'Battery may reach reserve' },
  breachImminent: { installer: 'Breach imminent', homeowner: 'Battery low' },

  // Severity labels
  critical: { installer: 'Critical', homeowner: 'Needs attention' },
  watch: { installer: 'Watch', homeowner: 'Monitor' },
  stable: { installer: 'Stable', homeowner: 'All good' },

  // Fleet/system terms
  offGrid: { installer: 'Off-Grid', homeowner: 'Battery powered' },
  gridTied: { installer: 'Grid-Tied', homeowner: 'Grid connected' },
  confidence: { installer: 'Confidence', homeowner: 'Forecast certainty' },
  clearSkyOutput: { installer: 'Clear-sky output', homeowner: 'Expected output' },
  underperformance: { installer: 'Underperformance', homeowner: 'Solar below expected' },

  // Dashboard labels
  backupReadiness: { installer: 'Backup Readiness', homeowner: 'Backup Power' },
  overnightOutlook: { installer: 'Overnight outlook', homeowner: 'Overnight outlook' },
  energyGuidance: { installer: 'Energy guidance', homeowner: 'Energy guidance' },
  batteryRunway: { installer: 'Battery Runway Forecast', homeowner: 'Battery Outlook' },

  // Forecast
  depletion: { installer: 'Depletion', homeowner: 'Battery may run out' },
  depletionTime: { installer: 'Breach in', homeowner: 'Battery may reach reserve in' },

  // Fleet status terms
  curtailing: { installer: 'Curtailing', homeowner: 'Solar limited' },
  underperforming: { installer: 'Underperforming', homeowner: 'Below expected' },

  // Settings
  systems: { installer: 'Systems', homeowner: 'Equipment' },
};

/**
 * Translate a term based on audience.
 * Returns the installer term if isHomeowner is false, homeowner term if true.
 * Falls back to the key itself if not found.
 */
export function t(key: string, isHomeowner: boolean): string {
  const entry = VOCABULARY[key];
  if (!entry) return key;
  return isHomeowner ? entry.homeowner : entry.installer;
}

/**
 * Get both terms for a key (useful for conditional rendering).
 */
export function getTerms(key: string): { installer: string; homeowner: string } | null {
  return VOCABULARY[key] ?? null;
}
